import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import Logo from '../../components/common/Logo.jsx';

export default function LiveViewPage() {
  const { token }      = useParams();
  const [info, setInfo]       = useState(null);
  const [error, setError]     = useState(null);
  const [status, setStatus]   = useState('connecting');
  const videoRef   = useRef(null);
  const socketRef  = useRef(null);
  const peerRef    = useRef(null);

  useEffect(() => {
    // Récupérer les infos du share via token
    fetch(`/api/live/${token}`)
      .then(r => r.json())
      .then(d => {
        if (!d.success) { setError(d.message); return; }
        setInfo(d.data);
        connectSocket(d.data);
      })
      .catch(() => setError('Lien invalide ou expiré'));

    return () => {
      socketRef.current?.disconnect();
      peerRef.current?.close();
    };
  }, [token]);

  function connectSocket(data) {
    const socket = io(window.location.origin, {
      auth: { publicToken: token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('viewer:join', { streamKey: data.streamKey });
    });

    socket.on('camera:online', () => setStatus('waiting'));
    socket.on('camera:offline', () => setStatus('offline'));

    socket.on('webrtc:offer', async ({ senderId, offer }) => {
      socket.cameraId = senderId;
      setStatus('streaming');
      const pc = new RTCPeerConnection({
        iceServers: data.iceServers || [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      peerRef.current = pc;
      pc.ontrack = (e) => {
        if (videoRef.current) videoRef.current.srcObject = e.streams[0];
      };
      pc.onicecandidate = (e) => {
        if (e.candidate) socket.emit('webrtc:ice-candidate', { targetId: senderId, candidate: e.candidate });
      };
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('webrtc:answer', { targetId: senderId, answer });
    });

    socket.on('webrtc:ice-candidate', ({ candidate }) => {
      peerRef.current?.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
    });
  }

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#060E1F', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <Logo size="md" />
      <p style={{ color: '#FF3B5C', fontSize: 16 }}>{error}</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#060E1F', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #1E3A5F' }}>
        <Logo size="sm" showText />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {info && <span style={{ color: '#94A3B8', fontSize: 13 }}>{info.deviceName}</span>}
          <span style={{
            padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: status === 'streaming' ? '#FF3B5C22' : '#1E3A5F',
            color: status === 'streaming' ? '#FF3B5C' : '#64748B',
          }}>
            {status === 'streaming' ? '● LIVE' : status === 'offline' ? 'HORS LIGNE' : 'CONNEXION...'}
          </span>
        </div>
      </div>

      {/* Vidéo */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ width: '100%', maxWidth: 960, aspectRatio: '16/9', background: '#0A1628', borderRadius: 12, overflow: 'hidden', position: 'relative' }}>
          <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          {status !== 'streaming' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
              <span style={{ fontSize: 48 }}>{status === 'offline' ? '📴' : '📡'}</span>
              <span style={{ color: '#64748B', fontSize: 14 }}>
                {status === 'offline' ? 'Caméra hors ligne' : 'En attente du flux...'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 24px', borderTop: '1px solid #1E3A5F', textAlign: 'center' }}>
        <span style={{ color: '#334155', fontSize: 11 }}>
          Flux sécurisé par ELVYRA — DEKANI SOFTWARE · {info?.label || 'Partage public'}
        </span>
      </div>
    </div>
  );
}
